import { useEffect, useMemo, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { TableSkeleton } from "@/components/loading-skeletons";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Plus, Pencil, Trash2, Search, Inbox, Wallet, Clock, CheckCircle2,
  FileText, Users, User, FileDown, Banknote,
} from "lucide-react";
import { toast } from "sonner";
import { downloadBillPdf } from "@/lib/invoice-pdf";

export const Route = createFileRoute("/_authenticated/student-bills")({
  head: () => ({ meta: [{ title: "Tagihan Siswa — MI Al-Munajah Admin" }] }),
  component: StudentBillsPage,
});

type BillStatus = "pending" | "waiting_verification" | "paid";

type Bill = {
  id: string;
  student_id: string;
  payment_type_id: string;
  invoice_number: string | null;
  title: string;
  amount: number;
  due_date: string;
  status: BillStatus;
  payment_id: string | null;
  notes: string | null;
  created_at: string;
  students?: { id: string; full_name: string; nisn: string | null; class_id: string | null; classes?: { name: string } | null } | null;
  payment_types?: { id: string; name: string } | null;
};

type StudentRow = { id: string; full_name: string; nisn: string | null; class_id: string | null; classes?: { name: string } | null };
type PaymentType = { id: string; name: string; default_amount: number; is_active: boolean };
type ClassRow = { id: string; name: string };

type StatusFilter = "all" | BillStatus;

const todayStr = () => new Date().toISOString().slice(0, 10);

function StudentBillsPage() {
  const [rows, setRows] = useCachedState<Bill[]>("student-bills:rows", []);
  const [students, setStudents] = useCachedState<StudentRow[]>("student-bills:students", []);
  const [types, setTypes] = useCachedState<PaymentType[]>("student-bills:types", []);
  const [classes, setClasses] = useCachedState<ClassRow[]>("student-bills:classes", []);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState<StatusFilter>("all");
  const [classF, setClassF] = useState<string>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Bill | null>(null);
  const [delId, setDelId] = useState<string | null>(null);
  const [cashBill, setCashBill] = useState<Bill | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const [billsRes, studentsRes, typesRes, classesRes] = await Promise.all([
      supabase.from("student_bills")
        .select("*, students(id, full_name, nisn, class_id, classes(name)), payment_types(id, name)")
        .order("created_at", { ascending: false }),
      supabase.from("students").select("id, full_name, nisn, class_id, classes(name)").order("full_name"),
      supabase.from("payment_types").select("id, name, default_amount, is_active").order("name"),
      supabase.from("classes").select("id, name").order("name"),
    ]);
    if (billsRes.error) toast.error("Gagal memuat tagihan: " + billsRes.error.message);
    else setRows((billsRes.data ?? []) as unknown as Bill[]);
    if (studentsRes.data) setStudents(studentsRes.data as unknown as StudentRow[]);
    if (typesRes.data) setTypes(typesRes.data as PaymentType[]);
    if (classesRes.data) setClasses(classesRes.data as ClassRow[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel("student-bills-rt")
      .on("postgres_changes", { event: "*", schema: "public", table: "student_bills" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusF !== "all" && r.status !== statusF) return false;
      if (classF !== "all" && r.students?.class_id !== classF) return false;
      if (!ql) return true;
      return (
        (r.students?.full_name ?? "").toLowerCase().includes(ql) ||
        (r.students?.nisn ?? "").toLowerCase().includes(ql) ||
        (r.invoice_number ?? "").toLowerCase().includes(ql) ||
        (r.payment_id ?? "").toLowerCase().includes(ql) ||
        r.title.toLowerCase().includes(ql)
      );
    });
  }, [rows, q, statusF, classF]);

  const stats = useMemo(() => {
    const total = rows.reduce((a, r) => a + Number(r.amount), 0);
    const unpaid = rows.filter((r) => r.status === "pending").reduce((a, r) => a + Number(r.amount), 0);
    const waiting = rows.filter((r) => r.status === "waiting_verification").length;
    const paid = rows.filter((r) => r.status === "paid").reduce((a, r) => a + Number(r.amount), 0);
    return { total, unpaid, waiting, paid };
  }, [rows]);

  const remove = async () => {
    if (!delId) return;
    setBusy(true);
    const { error } = await supabase.from("student_bills").delete().eq("id", delId);
    setBusy(false);
    setDelId(null);
    if (error) return toast.error("Gagal menghapus: " + error.message);
    toast.success("Tagihan dihapus");
    load();
  };

  return (
    <div>
      <PageHeader
        title="Tagihan Siswa"
        description="Kelola tagihan SPP, seragam, daftar ulang, dan tagihan lainnya per siswa atau per kelas."
        actions={
          <Button onClick={() => { setEditing(null); setCreateOpen(true); }}>
            <Plus className="w-4 h-4" /> Buat Tagihan
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard label="Total Tagihan" value={formatIDR(stats.total)} icon={FileText} tint="bg-primary/10 text-primary" />
        <StatCard label="Belum Bayar" value={formatIDR(stats.unpaid)} icon={Wallet} tint="bg-destructive/10 text-destructive" />
        <StatCard label="Menunggu Verifikasi" value={stats.waiting.toString()} icon={Clock} tint="bg-warning/10 text-warning" />
        <StatCard label="Lunas" value={formatIDR(stats.paid)} icon={CheckCircle2} tint="bg-success/10 text-success" />
      </div>

      <Card className="p-4 rounded-2xl">
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama siswa, NISN, no. invoice, ID transaksi, atau judul…" className="pl-9" />
          </div>
          <Select value={statusF} onValueChange={(v) => setStatusF(v as StatusFilter)}>
            <SelectTrigger className="md:w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="pending">Belum Bayar</SelectItem>
              <SelectItem value="waiting_verification">Menunggu Verifikasi</SelectItem>
              <SelectItem value="paid">Lunas</SelectItem>
            </SelectContent>
          </Select>
          <Select value={classF} onValueChange={setClassF}>
            <SelectTrigger className="md:w-48"><SelectValue placeholder="Kelas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kelas</SelectItem>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-xl border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>No. Invoice</TableHead>
                <TableHead>Siswa</TableHead>
                <TableHead>Kelas</TableHead>
                <TableHead>Tagihan</TableHead>
                <TableHead>Jenis</TableHead>
                <TableHead className="text-right">Nominal</TableHead>
                <TableHead>Jatuh Tempo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableSkeleton columns={9} />
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-12">
                    <div className="flex flex-col items-center text-muted-foreground gap-2">
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                        <Inbox className="w-6 h-6" />
                      </div>
                      <div className="font-medium text-foreground">Belum ada tagihan.</div>
                      <div className="text-xs">Klik &ldquo;Buat Tagihan&rdquo; untuk menerbitkan tagihan baru.</div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.invoice_number ?? "—"}</TableCell>
                  <TableCell className="font-medium">
                    {r.students?.full_name ?? "—"}
                    <div className="text-xs text-muted-foreground">{r.students?.nisn ?? "—"}</div>
                  </TableCell>
                  <TableCell>{r.students?.classes?.name ?? "—"}</TableCell>
                  <TableCell>{r.title}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{r.payment_types?.name ?? "—"}</TableCell>
                  <TableCell className="text-right font-semibold">{formatIDR(r.amount)}</TableCell>
                  <TableCell className="text-sm">
                    <DueDate date={r.due_date} status={r.status} />
                  </TableCell>
                  <TableCell><StatusBadge s={r.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      {r.status !== "paid" && (
                        <Button
                          size="sm"
                          variant="outline"
                          title="Bayar Tunai"
                          onClick={() => setCashBill(r)}
                          className="h-8 gap-1 border-success/40 text-success hover:bg-success/10 hover:text-success"
                        >
                          <Banknote className="w-4 h-4" /> Tunai
                        </Button>
                      )}
                      {r.invoice_number && (
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Download PDF Tagihan"
                          onClick={() => downloadBillPdf(r).catch((e) => toast.error("Gagal membuat PDF: " + (e?.message ?? e)))}
                        >
                          <FileDown className="w-4 h-4" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(r); setCreateOpen(true); }}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setDelId(r.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <CreateEditBillDialog
        open={createOpen}
        onOpenChange={(o) => { setCreateOpen(o); if (!o) setEditing(null); }}
        editing={editing}
        students={students}
        types={types.filter((t) => t.is_active || (editing && t.id === editing.payment_type_id))}
        classes={classes}
        onSaved={load}
      />

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus tagihan?</AlertDialogTitle>
            <AlertDialogDescription>
              Tagihan akan dihapus permanen. Riwayat pembayaran pada tabel payments tetap tersimpan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CashPaymentDialog bill={cashBill} onOpenChange={(o) => !o && setCashBill(null)} onSaved={load} />
    </div>
  );
}

function CashPaymentDialog({
  bill, onOpenChange, onSaved,
}: { bill: Bill | null; onOpenChange: (o: boolean) => void; onSaved: () => void }) {
  const [amount, setAmount] = useState<number>(0);
  const [payDate, setPayDate] = useState<string>(todayStr());
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (bill) {
      setAmount(Number(bill.amount));
      setPayDate(todayStr());
      setNotes("");
    }
  }, [bill]);

  const save = async () => {
    if (!bill) return;
    if (!amount || amount <= 0) return toast.error("Nominal tidak valid");
    if (!payDate) return toast.error("Tanggal bayar wajib diisi");
    setBusy(true);
    const { data: u } = await supabase.auth.getUser();
    const nowIso = new Date().toISOString();
    const { error } = await supabase.from("payments").insert({
      bill_id: bill.id,
      student_id: bill.student_id,
      student_name: bill.students?.full_name ?? null,
      nisn: bill.students?.nisn ?? null,
      class_name: bill.students?.classes?.name ?? null,
      bill_name: bill.title,
      payment_name: bill.title,
      amount,
      payment_method: "cash",
      status: "paid",
      payment_date: new Date(payDate).toISOString(),
      paid_at: nowIso,
      verified_at: nowIso,
      verified_by: u.user?.id ?? null,
      notes: notes.trim() ? `Tunai - ${notes.trim()}` : "Pembayaran tunai di sekolah",
    });
    setBusy(false);
    if (error) return toast.error("Gagal mencatat pembayaran: " + error.message);
    toast.success("Pembayaran tunai berhasil dicatat & diverifikasi");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={!!bill} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-success" /> Bayar Tunai
          </DialogTitle>
          <DialogDescription>
            Catat pembayaran tunai langsung di sekolah. Tagihan otomatis ditandai <b>Lunas</b> dan terverifikasi atas nama Anda.
          </DialogDescription>
        </DialogHeader>

        {bill && (
          <div className="rounded-lg border bg-muted/30 p-3 text-sm space-y-1">
            <div className="font-medium">{bill.students?.full_name ?? "—"} <span className="text-muted-foreground font-normal">· {bill.students?.classes?.name ?? "—"}</span></div>
            <div className="text-muted-foreground">{bill.title} <span className="font-mono text-xs">({bill.invoice_number ?? "—"})</span></div>
            <div className="text-muted-foreground">Total Tagihan: <span className="font-semibold text-foreground">{formatIDR(bill.amount)}</span></div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label>Nominal Diterima (Rp) <span className="text-destructive">*</span></Label>
            <Input
              inputMode="numeric"
              value={amount ? new Intl.NumberFormat("id-ID").format(amount) : ""}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                setAmount(digits ? Number(digits) : 0);
              }}
              placeholder="0"
            />
          </div>
          <div>
            <Label>Tanggal Bayar <span className="text-destructive">*</span></Label>
            <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
          </div>
          <div>
            <Label>Catatan</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opsional — mis. nama penerima / nomor kwitansi" rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={save} disabled={busy}>
            <Banknote className="w-4 h-4" /> Tandai Lunas
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateEditBillDialog({
  open, onOpenChange, editing, students, types, classes, onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: Bill | null;
  students: StudentRow[];
  types: PaymentType[];
  classes: ClassRow[];
  onSaved: () => void;
}) {
  const [mode, setMode] = useState<"single" | "class">("single");
  const [studentId, setStudentId] = useState<string>("");
  const [classId, setClassId] = useState<string>("");
  const [typeId, setTypeId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState<number>(0);
  const [dueDate, setDueDate] = useState<string>(todayStr());
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setMode("single");
      setStudentId(editing.student_id);
      setTypeId(editing.payment_type_id);
      setTitle(editing.title);
      setAmount(Number(editing.amount));
      setDueDate(editing.due_date);
      setNotes(editing.notes ?? "");
    } else {
      setMode("single");
      setStudentId("");
      setClassId("");
      setTypeId("");
      setTitle("");
      setAmount(0);
      setDueDate(todayStr());
      setNotes("");
    }
  }, [open, editing]);

  const onPickType = (id: string) => {
    setTypeId(id);
    const t = types.find((x) => x.id === id);
    if (t) {
      setTitle(t.name);
      setAmount(Number(t.default_amount));
    }
  };

  const save = async () => {
    if (!typeId) return toast.error("Pilih jenis pembayaran");
    if (!title.trim()) return toast.error("Judul tagihan wajib diisi");
    if (!amount || amount < 0) return toast.error("Nominal tidak valid");
    if (!dueDate) return toast.error("Jatuh tempo wajib diisi");

    setBusy(true);

    if (editing) {
      const { error } = await supabase.from("student_bills").update({
        payment_type_id: typeId,
        title: title.trim(),
        amount,
        due_date: dueDate,
        notes: notes.trim() || null,
      }).eq("id", editing.id);
      setBusy(false);
      if (error) return toast.error(error.message);
      toast.success("Tagihan diperbarui");
      onOpenChange(false);
      onSaved();
      return;
    }

    if (mode === "single") {
      if (!studentId) { setBusy(false); return toast.error("Pilih siswa"); }
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("student_bills").insert({
        student_id: studentId,
        payment_type_id: typeId,
        title: title.trim(),
        amount,
        due_date: dueDate,
        notes: notes.trim() || null,
        created_by: u.user?.id ?? null,
      });
      setBusy(false);
      if (error) return toast.error(error.message);
      toast.success("Tagihan dibuat");
      onOpenChange(false);
      onSaved();
    } else {
      if (!classId) { setBusy(false); return toast.error("Pilih kelas"); }
      const { data, error } = await supabase.rpc("generate_bills_for_class", {
        class_id_input: classId,
        payment_type_id_input: typeId,
        title_input: title.trim(),
        amount_input: amount,
        due_date_input: dueDate,
        notes_input: notes.trim() || undefined,
      });
      setBusy(false);
      if (error) return toast.error(error.message);
      toast.success(`Berhasil membuat ${data ?? 0} tagihan untuk kelas terpilih`);
      onOpenChange(false);
      onSaved();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Tagihan" : "Buat Tagihan"}</DialogTitle>
          <DialogDescription>
            {editing ? "Ubah nominal, jatuh tempo, atau detail tagihan." : "Terbitkan tagihan untuk satu siswa atau seluruh kelas."}
          </DialogDescription>
        </DialogHeader>

        {!editing && (
          <Tabs value={mode} onValueChange={(v) => setMode(v as "single" | "class")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="single"><User className="w-4 h-4 mr-1" /> Per Siswa</TabsTrigger>
              <TabsTrigger value="class"><Users className="w-4 h-4 mr-1" /> Per Kelas</TabsTrigger>
            </TabsList>
            <TabsContent value="single" className="mt-4">
              <Label>Siswa <span className="text-destructive">*</span></Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger><SelectValue placeholder="Pilih siswa" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.full_name} {s.classes?.name ? `· ${s.classes.name}` : ""} {s.nisn ? `· ${s.nisn}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </TabsContent>
            <TabsContent value="class" className="mt-4">
              <Label>Kelas <span className="text-destructive">*</span></Label>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                <SelectContent>
                  {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Tagihan akan dibuat untuk seluruh siswa di kelas ini.</p>
            </TabsContent>
          </Tabs>
        )}

        <div className="space-y-3">
          <div>
            <Label>Jenis Pembayaran <span className="text-destructive">*</span></Label>
            <Select value={typeId} onValueChange={onPickType}>
              <SelectTrigger><SelectValue placeholder="Pilih jenis" /></SelectTrigger>
              <SelectContent>
                {types.length === 0 && (
                  <div className="p-3 text-sm text-muted-foreground">Belum ada jenis aktif. Tambahkan di menu Jenis Pembayaran.</div>
                )}
                {types.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} · {formatIDR(t.default_amount)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Judul Tagihan <span className="text-destructive">*</span></Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Contoh: SPP November 2026" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Nominal (Rp) <span className="text-destructive">*</span></Label>
              <Input
                inputMode="numeric"
                value={amount ? new Intl.NumberFormat("id-ID").format(amount) : ""}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  setAmount(digits ? Number(digits) : 0);
                }}
                placeholder="0"
              />
            </div>
            <div>
              <Label>Jatuh Tempo <span className="text-destructive">*</span></Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Catatan</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opsional" rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={save} disabled={busy}>{editing ? "Simpan" : "Buat"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, icon: Icon, tint }: {
  label: string; value: string; icon: React.ComponentType<{ className?: string }>; tint: string;
}) {
  return (
    <Card className="p-4 rounded-2xl border-border/60">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tint} mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-xl sm:text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </Card>
  );
}

function StatusBadge({ s }: { s: BillStatus }) {
  const map: Partial<Record<BillStatus, { label: string; cls: string }>> = {
    pending: { label: "Belum Bayar", cls: "bg-muted text-muted-foreground border-border" },
    waiting_verification: { label: "Menunggu", cls: "bg-warning/15 text-warning border-warning/30" },
    paid: { label: "Lunas", cls: "bg-success/15 text-success border-success/30" },
  };
  const v = map[s] ?? { label: String(s ?? "-"), cls: "bg-muted text-muted-foreground border-border" };
  return <Badge variant="outline" className={v.cls}>{v.label}</Badge>;
}

function DueDate({ date, status }: { date: string; status: BillStatus }) {
  const d = new Date(date);
  const overdue = status !== "paid" && d < new Date(new Date().toDateString());
  return (
    <span className={overdue ? "text-destructive font-medium" : ""}>
      {d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}
      {overdue && <span className="text-xs ml-1">(lewat)</span>}
    </span>
  );
}

function formatIDR(n: number) {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Number(n) || 0);
}
